import { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { requestMagicLink } from '@/api/auth';
import { FacilitaRgpdClause } from '@/components/FacilitaRgpdClause';
import { trackEvent, trackScrollDepth, trackSectionView, GA_EVENTS } from '@/lib/analytics';

/* ───────────────────────────── hooks ───────────────────────────── */

function useFadeUp() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          obs.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function useCounter(target: number, duration = 1800) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  const onVisible = () => {
    if (started.current) return;
    started.current = true;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  return { count, onVisible };
}

function AnimatedCounter({ target, duration, suffix, prefix }: { target: number; duration?: number; suffix?: string; prefix?: string }) {
  const { count, onVisible } = useCounter(target, duration);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible();
          obs.unobserve(el);
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible]);

  // Show target value as fallback when count is 0 (before animation starts)
  const displayValue = count === 0 ? target : count;

  return (
    <span ref={ref} className="text-3xl md:text-4xl font-bold text-[#0F172A] block" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {prefix}{displayValue.toLocaleString('es-ES')}{suffix}
    </span>
  );
}

/* helper – smooth scroll to element */
function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/* ───────────────────────────── main ───────────────────────────── */

export function LandingPage() {
  const { accessToken } = useAuthStore();

  useEffect(() => {
    if (accessToken) return;
    return trackScrollDepth();
  }, [accessToken]);

  if (accessToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navigation />
      <HeroSection />
      <SocialProofBar />
      <ProblemSection />
      <HowItWorksSection />
      <FeaturesSection />
      <TestimonialsSection />
      <FounderStorySection />
      <TrustSection />
      <IntegrationsSection />
      <PricingSection />
      <FAQSection />
      <FinalCTASection />
      <FooterSection />
      <StickyCTA />
    </div>
  );
}

/* ─── 1. Navigation ─────────────────────────────────────────────── */

function Navigation() {
  const [open, setOpen] = useState(false);
  const [_scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Cómo funciona', href: 'como-funciona' },
    { label: 'Funcionalidades', href: 'funcionalidades' },
    { label: 'Testimonios', href: 'testimonios' },
    { label: 'Precios', href: 'precios' },
    { label: 'Blog', href: '/blog', external: true },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur shadow-sm"
    >
      <div className="max-w-[1100px] mx-auto px-5 h-16 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2"
          aria-label="Gestoria Lite - Inicio"
        >
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>GL</span>
          </div>
          <span className="font-bold text-[#0F172A] text-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Gestoria Lite
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            l.external ? (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors"
              >
                {l.label}
              </button>
            )
          ))}
          <button
            onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: 'nav' }); scrollTo('signup-form'); }}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            Empezar gratis
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 overflow-hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
        >
          <span className={`block w-5 h-0.5 bg-[#0F172A] transition-all duration-300 origin-center ${open ? 'rotate-45 translate-y-[8px]' : ''}`} />
          <span className={`block w-5 h-0.5 bg-[#0F172A] transition-all duration-300 origin-center ${open ? 'opacity-0 scale-0' : ''}`} />
          <span className={`block w-5 h-0.5 bg-[#0F172A] transition-all duration-300 origin-center ${open ? '-rotate-45 -translate-y-[8px]' : ''}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in-down">
          <div className="px-5 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              l.external ? (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-left text-[#64748B] hover:text-[#0F172A] py-2 text-sm"
                >
                  {l.label}
                </a>
              ) : (
                <button
                  key={l.href}
                  onClick={() => { scrollTo(l.href); setOpen(false); }}
                  className="text-left text-[#64748B] hover:text-[#0F172A] py-2 text-sm"
                >
                  {l.label}
                </button>
              )
            ))}
            <button
              onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: 'nav' }); scrollTo('signup-form'); setOpen(false); }}
              className="bg-[#2563EB] text-white text-sm font-medium px-5 py-2.5 rounded-lg mt-1"
            >
              Empezar gratis
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ─── 2. Hero Section ───────────────────────────────────────────── */

function HeroSection() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDpa, setAcceptedDpa] = useState(false);
  const formStartFired = useRef(false);

  const isValidEmail = (value: string): boolean => {
    if (value.length > 254) return false;
    const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicRegex.test(value)) return false;
    const localPart = value.split('@')[0];
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (localPart.includes('..')) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError('');

    if (!isValidEmail(email)) {
      setError('Por favor, introduce un email válido (ej: usuario@dominio.com)');
      trackEvent(GA_EVENTS.SIGNUP_FORM_ERROR, { error_message: 'invalid_email_format', page: 'landing' });
      return;
    }

    setLoading(true);
    trackEvent(GA_EVENTS.SIGNUP_FORM_SUBMIT, { page: 'landing' });
    try {
      await requestMagicLink(email);
      setSuccess(true);
      // Fire Google Ads conversion event
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 429) {
          setError('Demasiados intentos. Por favor, espera un minuto antes de volver a intentarlo.');
          trackEvent(GA_EVENTS.SIGNUP_FORM_ERROR, { error_message: 'rate_limit', page: 'landing' });
          return;
        }
        if (axiosError.response?.status === 400) {
          setError('Por favor, introduce un email válido (ej: usuario@dominio.com)');
          trackEvent(GA_EVENTS.SIGNUP_FORM_ERROR, { error_message: 'invalid_email_backend', page: 'landing' });
          return;
        }
      }
      setError('Ha ocurrido un error. Inténtalo de nuevo.');
      trackEvent(GA_EVENTS.SIGNUP_FORM_ERROR, { error_message: 'submission_failed', page: 'landing' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-28 pb-20 px-5 md:pt-32 md:pb-24 bg-white">
      <div className="max-w-[1100px] mx-auto">
        <div className="max-w-2xl mx-auto text-center">
          {/* Скрытый H1 для SEO с ключевыми словами */}
          <h1 className="sr-only">Software para Gestorías, Asesorías y Despachos de Abogados: Optimiza tu Despacho</h1>

          {/* Audience tag */}
          <p className="inline-block text-xs font-semibold tracking-wider uppercase text-[#2563EB] bg-blue-50 px-3 py-1.5 rounded-full mb-6">
            Para gestorías, asesorías y despachos de abogados en España
          </p>

          <p
            className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-[#0F172A] leading-tight mb-5"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Tu despacho sin caos documental.<br />Tus clientes más contentos.<br />Tú, más libre.
          </p>

          <p className="text-[18px] text-[#334155] mb-8 max-w-[560px] mx-auto leading-relaxed">
            Recibe documentos de tus clientes sin perseguirlos por WhatsApp. Sin que se registren. Sin caos. Organizado en 3 minutos.
            Se sincroniza automáticamente con Holded, Quipu y FacturaDirecta.
          </p>

          {/* Social proof mini */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <div className="flex -space-x-2">
              <img src="/images/avatars/avatar-mg.webp" alt="MG" className="w-8 h-8 rounded-full border-2 border-white object-cover shrink-0" width="32" height="32" />
              <img src="/images/avatars/avatar-jr.webp" alt="JR" className="w-8 h-8 rounded-full border-2 border-white object-cover shrink-0" width="32" height="32" />
              <img src="/images/avatars/avatar-lm.webp" alt="LM" className="w-8 h-8 rounded-full border-2 border-white object-cover shrink-0" width="32" height="32" />
              <img src="/images/avatars/avatar-ap.webp" alt="AP" className="w-8 h-8 rounded-full border-2 border-white object-cover shrink-0" width="32" height="32" />
              <img src="/images/avatars/avatar-cr.webp" alt="CR" className="w-8 h-8 rounded-full border-2 border-white object-cover shrink-0" width="32" height="32" />
            </div>
            <p className="text-sm text-[#64748B]">
              <span className="font-semibold text-[#0F172A]">+340 despachos</span> ya digitalizaron su gestión documental
            </p>
          </div>

          {/* CTA Card */}
          <div id="signup-form" className="max-w-md mx-auto bg-white rounded-xl shadow-lg border-2 border-[#2563EB] p-6 mb-8">
            {success ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Revisa tu email
                </h3>
                <p className="text-sm text-[#64748B]">
                  Te hemos enviado un enlace para acceder a tu cuenta.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2
                  className="text-lg font-bold text-[#0F172A] text-center mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Empieza gratis en 3 minutos
                </h2>
                <p className="text-sm text-[#64748B] mb-3 text-center">Introduce tu email para recibir un enlace de acceso</p>
                <input
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => { if (!formStartFired.current) { formStartFired.current = true; trackEvent(GA_EVENTS.SIGNUP_FORM_START, {}); } }}
                  required
                  className="w-full h-12 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent mb-3"
                />
                <label className="flex items-start gap-2.5 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => { setAcceptedTerms(e.target.checked); trackEvent(GA_EVENTS.CONSENT_CHECKBOX_TOGGLE, { checkbox: 'privacy', checked: e.target.checked }); }}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB] cursor-pointer"
                  />
                  <span className="text-[11px] text-[#64748B] leading-relaxed">
                    He leído y acepto la{' '}
                    <Link to="/privacy" className="text-[#2563EB] hover:underline">Política de Privacidad</Link>
                    {' '}y los{' '}
                    <Link to="/terms" className="text-[#2563EB] hover:underline">Términos de Uso</Link>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedDpa}
                    onChange={(e) => { setAcceptedDpa(e.target.checked); trackEvent(GA_EVENTS.CONSENT_CHECKBOX_TOGGLE, { checkbox: 'dpa', checked: e.target.checked }); }}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB] cursor-pointer"
                  />
                  <span className="text-[11px] text-[#64748B] leading-relaxed">
                    Acepto el{' '}
                    <Link to="/privacy#dpa" className="text-[#2563EB] hover:underline">contrato de encargo de tratamiento</Link>
                    {' '}(Art. 28 RGPD)
                  </span>
                </label>
                {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || !email || !acceptedTerms || !acceptedDpa}
                  className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors text-base cursor-pointer"
                >
                  {loading ? 'Enviando...' : 'Empezar gratis — sin tarjeta'}
                </button>
                <p className="text-xs text-center text-[#64748B] mt-3">
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="text-[#2563EB] hover:underline">Iniciar sesión</Link>
                </p>

                {/* Información de Protección de Datos (FACILITA RGPD / AEPD) */}
                <FacilitaRgpdClause />
              </form>
            )}
          </div>

          {/* Screenshot */}
          <div className="max-w-2xl mx-auto">
            <img
              src="/images/screenshot-dashboard.webp"
              srcSet="/images/screenshot-dashboard-640w.webp 640w, /images/screenshot-dashboard-1280w.webp 1280w, /images/screenshot-dashboard.webp 2560w"
              sizes="(max-width: 640px) 640px, (max-width: 1024px) 1280px, 2560px"
              alt="Dashboard de Gestoria Lite con resumen de clientes, documentos y plazos"
              className="w-full rounded-xl shadow-xl border border-gray-200"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              width="2560"
              height="1600"
            />
          </div>

          {/* Trust micro-badges */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-[#64748B]">
            {[
              { icon: 'shield-check', text: '14 días gratis' },
              { icon: 'lock', text: 'Cifrado AES-256 · Enlace temporal 1h' },
              { icon: 'server', text: 'Datos en UE' },
              { icon: 'shield', text: 'RGPD' },
            ].map((b) => (
              <span key={b.text} className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {b.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. Social Proof Bar (animated counters) ───────────────────── */

function SocialProofBar() {
  return (
    <section className="py-10 bg-[#F8FAFC] border-y border-gray-100">
      <div className="max-w-[1100px] mx-auto px-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          <div>
            <AnimatedCounter target={340} prefix="+" />
            <span className="text-sm text-[#64748B] mt-1 block">Despachos registrados</span>
          </div>
          <div>
            <AnimatedCounter target={12000} suffix="+" />
            <span className="text-sm text-[#64748B] mt-1 block">Documentos procesados</span>
          </div>
          <div>
            <AnimatedCounter target={1200} suffix="h" />
            <span className="text-sm text-[#64748B] mt-1 block">Horas ahorradas al mes</span>
          </div>
        </div>
        <p className="text-center text-xs text-[#2563EB] font-semibold mt-4">
          Clientes ilimitados en todos los planes
        </p>
      </div>
    </section>
  );
}

/* ─── 4. Problem Section ────────────────────────────────────────── */

function ProblemSection() {
  const ref = useFadeUp();

  const pains = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      stat: '73%',
      text: 'de los despachos pierde más de 3h/semana persiguiendo documentos por WhatsApp y email.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      stat: '1 de cada 5',
      text: 'documentos se pierde o llega tarde, provocando retrasos en presentaciones fiscales.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      ),
      stat: '40%',
      text: 'del tiempo productivo del profesional se consume en tareas administrativas repetitivas.',
    },
  ];

  return (
    <section className="py-20 md:py-24 px-5 bg-white">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            El problema que resolvemos
            <span className="sr-only"> — Digitalización para despachos</span>
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Si eres gestor, asesor o abogado, esto te sonará familiar
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {pains.map((p, i) => (
            <div key={i} className="bg-red-50/60 rounded-xl p-6 border border-red-100">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-500 mb-4">
                {p.icon}
              </div>
              <p className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {p.stat}
              </p>
              <p className="text-sm text-[#64748B] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 5. How It Works (Solution) ────────────────────────────────── */

function HowItWorksSection() {
  const ref = useFadeUp();

  const steps = [
    {
      num: '1',
      title: 'Envía el enlace al cliente',
      desc: 'Crea un cliente en 30 segundos. Comparte su enlace único por email o WhatsApp. Sin registro ni fricciones.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      ),
    },
    {
      num: '2',
      title: 'El cliente sube sin registrarse',
      desc: 'Tu cliente abre el enlace, arrastra documentos y listo. Sin contraseñas, sin apps, desde cualquier dispositivo.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
    },
    {
      num: '3',
      title: 'Gestiona desde un panel centralizado',
      desc: 'Visualiza checklists con progreso, aprueba o rechaza documentos y controla los plazos fiscales de cada cliente.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
        </svg>
      ),
    },
  ];

  return (
    <section id="como-funciona" className="py-20 md:py-24 px-5 bg-[#F8FAFC]">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Cómo funciona
            <span className="sr-only"> — Comunicación fluida con clientes</span>
          </h2>
          <p className="text-lg text-[#64748B]">
            Empieza a recibir documentos de tus clientes en minutos
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.num} className="relative bg-white rounded-xl p-7 border border-gray-200 hover:shadow-md transition-shadow">
              <span
                className="absolute -top-4 -left-2 w-9 h-9 bg-[#2563EB] text-white rounded-full flex items-center justify-center text-sm font-bold"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {s.num}
              </span>
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-[#2563EB] mb-5">
                {s.icon}
              </div>
              <h3
                className="text-lg font-semibold text-[#0F172A] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {s.title}
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Interactive carousel */}
        <StepsCarousel />

        {/* CTA after how it works */}
        <SectionCTA text="Empezar gratis — sin tarjeta" />
      </div>
    </section>
  );
}

/* ─── Steps Carousel ────────────────────────────────────────────── */

function StepsCarousel() {
  const [active, setActive] = useState(0);

  const slides = [
    {
      label: '1. Crea el expediente',
      img: '/images/screenshot-clients.webp',
      srcSet: '/images/screenshot-clients-640w.webp 640w, /images/screenshot-clients-1280w.webp 1280w, /images/screenshot-clients.webp 2560w',
      alt: 'Panel de gestión documental: crea expedientes de clientes en Gestoria Lite',
      desc: 'Crea el expediente del cliente en segundos. Define qué documentos necesitas y el sistema se encarga del resto.',
    },
    {
      label: '2. Cliente sube docs',
      img: '/images/screenshot-portal.webp',
      srcSet: '/images/screenshot-portal-640w.webp 640w, /images/screenshot-portal-1280w.webp 1280w, /images/screenshot-portal.webp 2560w',
      alt: 'Portal de clientes sin registro: sube documentos desde móvil',
      desc: 'Tu cliente recibe un enlace y sube los documentos desde el móvil. Sin instalar nada, sin registrarse, sin llamadas.',
    },
    {
      label: '3. Revisas y gestionas',
      img: '/images/screenshot-detail.webp',
      srcSet: '/images/screenshot-detail-640w.webp 640w, /images/screenshot-detail-1280w.webp 1280w, /images/screenshot-detail.webp 2560w',
      alt: 'Panel de control para despachos: revisión y aprobación de documentos',
      desc: 'Revisas, apruebas o rechazas con un clic. Todo organizado por cliente, tipo y plazo fiscal.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const move = (dir: number) => {
    setActive((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <div className="mt-14 max-w-[800px] mx-auto relative">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all cursor-pointer border ${
              active === i
                ? 'bg-[#2563EB] text-white border-[#2563EB]'
                : 'bg-white text-[#64748B] border-gray-200 hover:border-gray-300'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Slide area */}
      <div className="relative rounded-2xl overflow-hidden shadow-xl bg-[#F8FAFC] border border-gray-200">
        {slides.map((s, i) => (
          <div
            key={i}
            className="flex flex-col items-center p-6 md:p-8 transition-opacity duration-300"
            style={{ display: active === i ? 'flex' : 'none' }}
          >
            <div className="w-full rounded-xl overflow-hidden border border-gray-200 bg-white" style={{ aspectRatio: '16/10' }}>
              <img
                src={s.img}
                srcSet={s.srcSet}
                sizes="(max-width: 640px) 640px, (max-width: 1024px) 800px, 800px"
                alt={s.alt}
                className="w-full h-full object-contain"
                loading="lazy"
                width="2560"
                height="1600"
              />
            </div>
            <p className="mt-4 text-sm text-[#64748B] text-center max-w-[520px] leading-relaxed">
              {s.desc}
            </p>
          </div>
        ))}

        {/* Arrows */}
        <button
          onClick={() => move(-1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-[#64748B] hover:text-[#0F172A] shadow-sm transition-colors cursor-pointer"
          aria-label="Anterior"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => move(1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-[#64748B] hover:text-[#0F172A] shadow-sm transition-colors cursor-pointer"
          aria-label="Siguiente"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              active === i ? 'bg-[#2563EB] w-6' : 'bg-gray-300'
            }`}
            aria-label={`Ir al paso ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Reusable section CTA ──────────────────────────────────────── */

function SectionCTA({ text = 'Empieza gratis — sin tarjeta' }: { text?: string }) {
  return (
    <div className="text-center mt-10">
      <button
        onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: 'section' }); scrollTo('signup-form'); }}
        className="inline-flex h-12 px-8 items-center justify-center bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-lg text-base transition-colors cursor-pointer"
      >
        {text}
      </button>
      <p className="mt-2.5 text-[13px] text-[#64748B]">
        Configuración en 3 minutos &middot; Cancela cuando quieras
      </p>
    </div>
  );
}

/* ─── 6. Features ───────────────────────────────────────────────── */

function FeaturesSection() {
  const ref = useFadeUp();

  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Portal de clientes sin registro',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> perseguías documentos por WhatsApp durante días.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> el cliente lo sube solo en 3 minutos desde su móvil.</>),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      title: 'Organización automática',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> no sabías si el cliente había visto tu mensaje o simplemente lo ignoraba.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> ves al instante qué documentos están aprobados, pendientes o rechazados.</>),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      title: 'Recordatorios de plazos',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> tú recordabas manualmente a cada cliente qué faltaba.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> el sistema avisa automáticamente hasta que sube el documento.</>),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: 'Checklists de documentos',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> perseguías a cada cliente con emails y mensajes para saber qué documentos faltaban.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> creas un checklist y el cliente ve exactamente qué debe subir, sin que le expliques nada.</>),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Seguridad RGPD',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> documentos de clientes en el móvil, en el email, en carpetas sin control.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> todo cifrado y almacenado en servidores en España. RGPD compliant.</>),
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Configuración en 3 minutos',
      desc: (<><span style={{ color: '#94A3B8' }}>Antes:</span> implementar un software nuevo significaba semanas de formación y migración.<br /><span style={{ color: '#10B981', fontWeight: 600 }}>Ahora:</span> creas tu cuenta, configuras tus clientes y empiezas a recibir documentos en 3 minutos.</>),
    },
  ];

  return (
    <section id="funcionalidades" className="py-20 md:py-24 px-5 bg-white">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Todo lo que necesitas para dejar atrás el caos
            <span className="sr-only"> — Gestión documental inteligente</span>
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Diseñado específicamente para gestorías, asesorías y despachos de abogados en España
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div
              key={i}
              className="bg-[#F8FAFC] rounded-xl p-8 border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-[#2563EB] mb-4">
                {f.icon}
              </div>
              <h3
                className="text-base font-semibold text-[#0F172A] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {f.title}
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 7. Testimonials ───────────────────────────────────────────── */

function TestimonialsSection() {
  const ref = useFadeUp();

  const testimonials = [
    {
      quote: 'Antes perdía 4 horas a la semana pidiendo documentos por WhatsApp. Ahora envío el enlace y me olvido — los clientes suben todo solos. He recuperado una mañana entera.',
      name: 'Marta G.',
      role: 'Gestora administrativa',
      location: 'Barcelona',
      initials: 'MG',
      bg: 'bg-blue-500',
      metric: 'Ahorra 4h/semana',
      avatarImg: '/images/avatars/avatar-mg.webp',
    },
    {
      quote: 'Lo que más me gusta es el checklist de TIE. Mis clientes extranjeros saben exactamente qué les falta sin que yo tenga que explicarlo. He reducido las llamadas un 60%.',
      name: 'Javier R.',
      role: 'Asesor fiscal',
      location: 'Madrid',
      initials: 'JR',
      bg: 'bg-emerald-500',
      metric: '-60% llamadas',
      avatarImg: '/images/avatars/avatar-jr.webp',
    },
    {
      quote: 'Llevamos dos meses usándolo y ya no tenemos ni un solo documento perdido en el email. Para el precio que tiene, es imprescindible. Mi equipo lo adoptó en un día.',
      name: 'Laura M.',
      role: 'Responsable documental',
      location: 'Valencia',
      initials: 'LM',
      bg: 'bg-purple-500',
      metric: '0 documentos perdidos',
      avatarImg: '/images/avatars/avatar-lm.webp',
    },
  ];

  return (
    <section id="testimonios" className="py-20 md:py-24 px-5 bg-[#F8FAFC]">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Lo que dicen los profesionales
          </h2>
          <p className="text-lg text-[#64748B]">
            Resultados reales de profesionales como tú
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col">
              {/* Metric badge */}
              <span className="inline-flex self-start items-center gap-1.5 bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 border border-green-200">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t.metric}
              </span>

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, j) => (
                  <svg key={j} className="h-4 w-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              <p className="text-sm text-[#64748B] leading-relaxed mb-5 flex-1">"{t.quote}"</p>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <img
                  src={t.avatarImg}
                  alt={t.name}
                  className="w-10 h-10 rounded-full shrink-0 object-cover"
                  loading="lazy"
                  width="40"
                  height="40"
                />
                <div>
                  <p className="text-sm font-semibold text-[#0F172A]">{t.name}</p>
                  <p className="text-xs text-[#64748B]">{t.role} &middot; {t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA after testimonials */}
        <SectionCTA text="Empezar gratis — sin tarjeta" />
      </div>
    </section>
  );
}

/* ─── 8. Founder Story ──────────────────────────────────────────── */

function FounderStorySection() {
  const ref = useFadeUp();

  return (
    <section className="py-20 md:py-24 px-5 bg-white">
      <div ref={ref} className="max-w-[800px] mx-auto fade-up">
        <div className="text-center mb-8">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Por qué creé Gestoría Lite
          </h2>
        </div>

        <div className="bg-[#F8FAFC] rounded-2xl p-8 md:p-10 border border-gray-200">
          <div className="flex gap-4 mb-6">
            <img
              src="/images/founder-yevhen-cropped.webp"
              alt="Yevhen Klymenko, fundador de Gestoria Lite"
              className="w-12 h-12 rounded-full shrink-0 object-cover"
              loading="lazy"
              width="48"
              height="48"
            />
            <div>
              <p className="font-semibold text-[#0F172A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Yevhen Klymenko</p>
              <p className="text-xs text-[#64748B]">Fundador · Gestoria Lite · Barcelona</p>
            </div>
          </div>
          <p className="text-[#0F172A] leading-relaxed mb-4 text-lg">
            Vi a mi gestor desbordado: <strong>40 clientes, 200 WhatsApps al mes</strong>, documentos perdidos en el email.
            Una persona brillante haciendo de mensajero digital.
          </p>
          <p className="text-[#0F172A] leading-relaxed mb-4 text-lg">
            <strong>Gestoria Lite</strong> nació para que los profesionales dejen de buscar documentos
            y vuelvan a hacer lo único que importa: <em>asesorar</em>.
          </p>
          <p className="text-[#64748B] text-sm">
            Si te sientes identificado, prueba 14 días gratis. Sin tarjeta, sin compromiso.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── 9. Trust & Compliance ─────────────────────────────────────── */

function TrustSection() {
  const ref = useFadeUp();

  const badges = [
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: 'Cifrado AES-256',
      desc: 'En reposo y en tránsito. Acceso por enlace temporal de 1h',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      ),
      title: 'Servidores en la UE',
      desc: 'Datos almacenados en Cloudflare R2 (Europa)',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Cumplimiento RGPD',
      desc: 'Acceso, rectificación, supresión y portabilidad',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      title: 'Auditoría de accesos',
      desc: 'Registro completo de quién accede a cada documento',
    },
  ];

  return (
    <section className="py-20 md:py-24 px-5 bg-[#F8FAFC]">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Seguridad y cumplimiento normativo
          </h2>
          <p className="text-lg text-[#64748B]">
            Tus datos y los de tus clientes están protegidos
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {badges.map((b, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 text-center">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-[#10B981]">
                {b.icon}
              </div>
              <h3
                className="font-semibold text-sm text-[#0F172A] mb-1"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {b.title}
              </h3>
              <p className="text-xs text-[#64748B]">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 10. Integrations ──────────────────────────────────────────── */

const INTEGRATIONS = [
  { name: 'Holded',          logo: '/images/holded-logo.svg',          logoPadding: 'p-3' },
  { name: 'Quipu',           logo: '/images/quipu-logo.svg',           logoPadding: 'p-2' },
  { name: 'FacturaDirecta',  logo: '/images/facturadirecta-logo.png',  logoPadding: 'p-3' },
];

function IntegrationsSection() {
  const ref = useFadeUp();

  return (
    <section className="py-16 md:py-20 px-5 bg-white">
      <div ref={ref} className="max-w-[800px] mx-auto fade-up">
        <div className="text-center mb-10">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Conectado con tus herramientas
          </h2>
          <p className="text-lg text-[#64748B]">
            Sincroniza tu despacho con los servicios que ya utilizas
          </p>
        </div>

        <div className="space-y-4">
          {INTEGRATIONS.map(({ name, logo, logoPadding }) => (
            <div key={name} className="bg-[#F8FAFC] rounded-2xl border border-gray-200 p-8 md:p-10">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className={`w-16 h-16 bg-white rounded-2xl border border-gray-200 flex items-center justify-center shrink-0 shadow-sm ${logoPadding}`}>
                  <img src={logo} alt={name} className="w-full h-full object-contain" />
                </div>

                <div className="text-center sm:text-left flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                    <h3
                      className="text-lg font-semibold text-[#0F172A]"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {name}
                    </h3>
                    <span className="inline-flex self-center sm:self-auto items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-green-200">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Disponible
                    </span>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    Sincronización automática de clientes con {name}. Al crear o eliminar un cliente en Gestoria Lite,
                    los cambios se reflejan automáticamente en tu cuenta de {name}.
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-[#64748B]">
          {['Sincronización automática', 'Cifrado AES-256', 'Todos los planes'].map((item) => (
            <span key={item} className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 11. FAQ ───────────────────────────────────────────────────── */

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const ref = useFadeUp();

  const faqs = [
    {
      q: '¿Mis clientes mayores o poco tecnológicos sabrán usarlo?',
      a: 'Sí. El portal del cliente no requiere registro, contraseña ni instalación. Tu cliente recibe un enlace, lo abre desde su móvil o ordenador, y sube el documento en un solo paso. Lo hemos probado con clientes de 70+ años sin asistencia. Si un cliente tiene problemas, tú puedes reenviarle el enlace en cualquier momento.',
    },
    {
      q: '¿Es seguro almacenar documentos en Gestoria Lite?',
      a: 'Sí. Utilizamos cifrado SSL en tránsito y los documentos se almacenan en servidores de Cloudflare R2 en la Unión Europea, cumpliendo con el RGPD.',
    },
    {
      q: '¿Mis clientes necesitan registrarse?',
      a: 'No. Cada cliente recibe un enlace único donde puede subir documentos de forma segura sin crear cuenta ni contraseñas.',
    },
    {
      q: '¿Hay permanencia o compromiso?',
      a: 'No. Puedes cancelar tu cuenta en cualquier momento sin penalización. Al cancelar, tienes 30 días para descargar tus datos.',
    },
    {
      q: '¿El precio incluye IVA?',
      a: 'No. El precio mostrado es sin IVA. Se aplicará el 21% de IVA español en la factura final.',
    },
    {
      q: '¿Qué pasa después de los 14 días de prueba?',
      a: 'Puedes elegir el plan Solo por 12 EUR/mes o continuar con acceso limitado. No se requiere tarjeta de crédito para el trial.',
    },
    {
      q: '¿Puedo exportar mis datos?',
      a: 'Sí. Puedes descargar todos los documentos de tus clientes en cualquier momento. También puedes solicitar una exportación completa según el RGPD.',
    },
    {
      q: '¿Cuánto tiempo puedo ahorrar usando Gestoria Lite?',
      a: 'Los profesionales ahorran en promedio 3-4 horas semanales que antes dedicaban a perseguir documentos por WhatsApp o email. El sistema automatiza el intercambio documental y los recordatorios.',
    },
    {
      q: '¿Cumple Gestoria Lite con el RGPD y la protección de datos?',
      a: 'Sí, cumplimos estrictamente con la normativa española y europea. Todos los documentos se almacenan cifrados en servidores de la Unión Europea (Cloudflare R2), utilizamos cifrado SSL/TLS en tránsito, y cumplimos con todos los requisitos del RGPD incluyendo derechos de acceso, rectificación y supresión.',
    },
    {
      q: '¿Qué beneficios aporta la digitalización para asesorías, gestorías y despachos de abogados?',
      a: 'La digitalización elimina el caos documental, mejora la experiencia del cliente, reduce documentos perdidos a cero y permite al profesional enfocarse en asesorar en lugar de tareas administrativas repetitivas.',
    },
    {
      q: '¿Con qué software de facturación y contabilidad se integra Gestoria Lite?',
      a: 'Actualmente integramos con Holded, Quipu y FacturaDirecta. Al crear o actualizar un cliente en Gestoria Lite, sus datos (nombre, email, teléfono, NIF/NIE y notas internas) se sincronizan automáticamente con el software que tengas configurado. Seguiremos añadiendo más integraciones según las solicitudes de nuestros usuarios.',
    },
  ];

  return (
    <section className="py-20 md:py-24 px-5 bg-white">
      <div ref={ref} className="max-w-[700px] mx-auto fade-up">
        <div className="text-center mb-14">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Preguntas frecuentes
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => {
                  const isOpening = openIdx !== i;
                  setOpenIdx(openIdx === i ? null : i);
                  if (isOpening) trackEvent(GA_EVENTS.FAQ_INTERACTION, { question_index: i, question_text: f.q.substring(0, 100) });
                }}
                className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                aria-expanded={openIdx === i}
              >
                <span className="font-medium text-[#0F172A] text-sm pr-4">{f.q}</span>
                <svg
                  className={`w-5 h-5 text-[#64748B] shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIdx === i && (
                <div className="px-6 pb-4 bg-white">
                  <p className="text-sm text-[#64748B] leading-relaxed">{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 11. Pricing ───────────────────────────────────────────────── */

function PricingSection() {
  const ref = useFadeUp();
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    return trackSectionView('precios', GA_EVENTS.PRICING_VIEW);
  }, []);

  const plans = [
    {
      name: 'Solo',
      badge: 'Para el profesional independiente',
      priceMonthly: 12,
      priceAnnual: 120,
      description: 'Todo lo que necesitas para gestionar tu despacho tú solo, sin complicaciones.',
      cta: 'Empezar gratis — sin tarjeta',
      ctaAction: () => scrollTo('signup-form'),
      highlight: false,
      features: [
        { text: '1 usuario', soon: false },
        { text: 'Clientes ilimitados', soon: false },
        { text: 'Almacenamiento: 20 GB', soon: false },
        { text: 'Checklists y documentos', soon: false },
        { text: 'Portal del cliente sin instalación', soon: false },
        { text: 'Recordatorios automáticos por email', soon: false },
        { text: 'Plazos y vencimientos', soon: false },
        { text: 'Sincronización con Holded, Quipu y FacturaDirecta', soon: false },
        { text: 'Soporte por email', soon: false },
      ],
    },
    {
      name: 'Team',
      badge: 'Para despachos en crecimiento',
      priceMonthly: 29,
      priceAnnual: 290,
      description: 'Colabora con tu equipo, automatiza más y ofrece una experiencia profesional a tus clientes.',
      cta: 'Empezar gratis — sin tarjeta',
      ctaAction: () => scrollTo('signup-form'),
      highlight: true,
      features: [
        { text: 'Hasta 5 usuarios', soon: false },
        { text: 'Clientes ilimitados', soon: false },
        { text: 'Almacenamiento: 100 GB', soon: false },
        { text: 'Todo lo del plan Solo, más:', soon: false },
        { text: 'Asignación de clientes por profesional', soon: false },
        { text: 'Branding personalizado en el portal', soon: false },
        { text: 'Asignaciones masivas de checklists', soon: false },
        { text: 'Estadísticas del equipo', soon: false },
        { text: 'Recordatorios automáticos de checklists', soon: false },
        { text: 'Exportación de datos (CSV)', soon: false },
        { text: 'Soporte prioritario', soon: false },
      ],
    },
    {
      name: 'Pro',
      badge: 'Para despachos consolidados',
      priceMonthly: 59,
      priceAnnual: 590,
      description: 'Máxima automatización para despachos con alto volumen de clientes y equipo amplio.',
      cta: 'Empezar gratis — sin tarjeta',
      ctaAction: () => scrollTo('signup-form'),
      highlight: false,
      features: [
        { text: 'Hasta 15 usuarios', soon: false },
        { text: 'Clientes ilimitados', soon: false },
        { text: 'Almacenamiento: 500 GB', soon: false },
        { text: 'Todo lo del plan Team, más:', soon: false },
        { text: 'Notificaciones por WhatsApp', soon: true },
        { text: 'IA para clasificación de documentos', soon: true },
        { text: 'Plazos recurrentes (configuración avanzada)', soon: false },
        { text: 'Exportación avanzada con filtros', soon: false },
        { text: 'API access', soon: true },
        { text: 'Gestor de cuenta dedicado', soon: false },
      ],
    },
  ];

  return (
    <section id="precios" className="py-20 md:py-24 px-5 bg-[#F8FAFC]">
      <div ref={ref} className="max-w-[1100px] mx-auto fade-up">
        <div className="text-center mb-10">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Precio simple y transparente
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Sin límites de clientes. Pagas por tu equipo, no por tus datos. Cancela cuando quieras.
          </p>
          <p className="text-center text-sm text-gray-500 mt-2 mb-6">
            <span className="line-through text-gray-400">Otros cobran 40–80€/mes</span>
            {' '}por menos funciones y con límite de clientes.
          </p>
        </div>

        {/* Annual / Monthly toggle */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm font-medium ${!annual ? 'text-[#0F172A]' : 'text-[#64748B]'}`}>Mensual</span>
          <button
            onClick={() => setAnnual(!annual)}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${annual ? 'bg-[#2563EB]' : 'bg-gray-300'}`}
            aria-label="Cambiar a facturación anual"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${annual ? 'translate-x-6' : ''}`} />
          </button>
          <span className={`text-sm font-medium ${annual ? 'text-[#0F172A]' : 'text-[#64748B]'}`}>
            Anual <span className="text-xs text-green-600 font-semibold">(ahorra 2 meses)</span>
          </span>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-xl p-7 flex flex-col ${
                plan.highlight
                  ? 'border-2 border-[#2563EB] shadow-xl md:-mt-2 md:mb-2'
                  : 'border border-gray-200 shadow-sm'
              }`}
            >
              {/* "Más popular" badge */}
              {plan.highlight && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2563EB] text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                  Más popular
                </span>
              )}

              <p className="text-xs font-medium text-[#2563EB] bg-blue-50 inline-block self-start px-2.5 py-1 rounded-full mb-3">
                {plan.badge}
              </p>
              <h3 className="text-xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {plan.name}
              </h3>
              <p className="text-sm text-[#64748B] mb-4 leading-relaxed min-h-[40px]">{plan.description}</p>

              <div className="mb-1">
                <span className="text-4xl font-bold text-[#0F172A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {annual ? plan.priceAnnual : plan.priceMonthly}&#8364;
                </span>
                <span className="text-[#64748B]"> / {annual ? 'año' : 'mes'}</span>
                <span className="text-gray-400 text-sm ml-1">+ IVA</span>
              </div>
              <div className="mt-2 mb-1">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  Precio de lanzamiento
                </span>
              </div>
              {annual && (
                <p className="text-xs text-green-600 mb-4">
                  Equivale a {Math.round(plan.priceAnnual / 12)}&#8364;/mes
                </p>
              )}
              {!annual && <div className="mb-4" />}

              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#0F172A]">
                    <svg className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>
                      {f.text}
                      {f.soon && (
                        <span className="ml-1.5 text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          próximamente
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: `pricing_${plan.name.toLowerCase()}` }); plan.ctaAction(); }}
                className={`w-full h-12 font-semibold rounded-lg transition-colors cursor-pointer ${
                  plan.highlight
                    ? 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white'
                    : 'bg-white border-2 border-[#2563EB] text-[#2563EB] hover:bg-blue-50'
                }`}
              >
                {plan.cta}
              </button>
              {plan.name === 'Pro' && (
                <p className="text-xs text-center text-[#94A3B8] mt-2">
                  ¿Más de 15 usuarios?{' '}
                  <a href="mailto:hola@gestorialite.com" className="underline hover:text-[#2563EB]">Escríbenos</a>
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Trust line */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-[#64748B]">
          {[
            '14 días gratis',
            'Sin tarjeta de crédito',
            'Cancela cuando quieras',
            'Clientes ilimitados en todos los planes',
          ].map((item) => (
            <span key={item} className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 12. Final CTA Banner ──────────────────────────────────────── */

function FinalCTASection() {
  const ref = useFadeUp();

  return (
    <section className="py-20 md:py-24 px-5 bg-[#2563EB]">
      <div ref={ref} className="max-w-[700px] mx-auto text-center fade-up">
        {/* Avatar stack */}
        <div className="flex justify-center -space-x-2 mb-4">
          <img src="/images/avatars/avatar-mg.webp" alt="Marta G." className="w-9 h-9 rounded-full border-2 border-[#2563EB] object-cover shrink-0" loading="lazy" width="36" height="36" />
          <img src="/images/avatars/avatar-jr.webp" alt="Javier R." className="w-9 h-9 rounded-full border-2 border-[#2563EB] object-cover shrink-0" loading="lazy" width="36" height="36" />
          <img src="/images/avatars/avatar-lm.webp" alt="Laura M." className="w-9 h-9 rounded-full border-2 border-[#2563EB] object-cover shrink-0" loading="lazy" width="36" height="36" />
          <img src="/images/avatars/avatar-ap.webp" alt="Ana P." className="w-9 h-9 rounded-full border-2 border-[#2563EB] object-cover shrink-0" loading="lazy" width="36" height="36" />
          <img src="/images/avatars/avatar-cr.webp" alt="Carlos R." className="w-9 h-9 rounded-full border-2 border-[#2563EB] object-cover shrink-0" loading="lazy" width="36" height="36" />
        </div>

        <p className="text-blue-200 text-sm mb-4">+340 despachos ya dentro</p>

        <h2
          className="text-3xl md:text-4xl font-bold text-white mb-4"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          Hoy es un buen día para dejar de perseguir documentos
        </h2>

        <p className="text-blue-100 mb-8 text-lg max-w-lg mx-auto">
          Configúralo en 3 minutos. Sin tarjeta, sin compromiso. 14 días gratis.
        </p>

        <button
          onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: 'final' }); scrollTo('signup-form'); }}
          className="inline-flex h-12 px-8 items-center justify-center bg-white text-[#2563EB] font-semibold rounded-lg text-base shadow-lg hover:bg-blue-50 transition-colors cursor-pointer"
        >
          Empezar gratis — sin tarjeta
        </button>
        <p className="text-blue-200 text-xs mt-3">Desde 12 EUR/mes + IVA &middot; Clientes ilimitados &middot; Cancela cuando quieras</p>
      </div>
    </section>
  );
}

/* ─── 13. Footer ────────────────────────────────────────────────── */

function FooterSection() {
  return (
    <footer className="bg-[#0F172A] text-white pt-14 pb-24 px-5">
      <div className="max-w-[1100px] mx-auto">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>GL</span>
              </div>
              <span className="font-bold text-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>Gestoria Lite</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-sm">
              Gestión documental moderna para gestorías, asesorías y despachos de abogados en España.
              Simplifica tu trabajo y mejora la experiencia de tus clientes.
            </p>
            <a
              href="mailto:hola@gestorialite.com"
              className="text-gray-400 hover:text-white transition-colors text-sm block mb-3"
            >
              hola@gestorialite.com
            </a>
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/gestorialite/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn de Gestoria Lite"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a
                href="https://x.com/gestorialite"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="X (Twitter) de Gestoria Lite"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Legal</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Política de privacidad
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Términos de uso
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Política de cookies
                </Link>
              </li>
              <li>
                <Link to="/aviso-legal" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Aviso legal
                </Link>
              </li>
              <li>
                <a href="/docs/DPA-acuerdo-encargado-tratamiento.pdf" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Encargado del Tratamiento (DPA)
                </a>
              </li>
            </ul>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Producto</h4>
            <ul className="space-y-2.5">
              <li>
                <button onClick={() => scrollTo('como-funciona')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Cómo funciona
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('funcionalidades')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Funcionalidades
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('testimonios')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Testimonios
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('precios')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Precios
                </button>
              </li>
              <li>
                <a href="/blog" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Blog
                </a>
              </li>
              <li>
                <a href="/alternativas" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Comparativa
                </a>
              </li>
              <li>
                <a href="/novedades" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Novedades
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-10 pt-8 text-center text-gray-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Gestoria Lite. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
}

/* ─── Sticky CTA bar ────────────────────────────────────────────── */

function StickyCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const signupForm = document.getElementById('signup-form');
    if (!signupForm) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        // Show sticky CTA when signup form scrolls out of view (above viewport)
        setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      },
      { threshold: 0 },
    );
    obs.observe(signupForm);
    return () => obs.disconnect();
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 py-3 px-5 z-50">
      <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-4">
        <div className="hidden sm:flex flex-col">
          <span className="text-sm font-semibold text-[#0F172A]">
            12 EUR/mes + IVA
          </span>
          <span className="text-xs text-[#64748B]">
            +340 despachos &middot; 14 días gratis
          </span>
        </div>
        <button
          onClick={() => { trackEvent(GA_EVENTS.CTA_CLICK, { location: 'sticky' }); scrollTo('signup-form'); }}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10 px-6 text-sm font-medium rounded-lg transition-colors sm:ml-auto cursor-pointer"
        >
          Empezar gratis — sin tarjeta
        </button>
      </div>
    </div>
  );
}
